import React, { useState, useEffect, useMemo } from "react";
import {
    Drawer,
    Button,
    Typography,
    Box,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MultiSelect from "components/SolsticeComponents/MultiSelect";
import DateFilterSelect from "components/DateFilterSelect";
import { NOTICE_DATE_FILTER_OPTIONS, resolveNoticeDatePresetDates, LAST_COMMENT_DATE_FILTER_OPTIONS, resolveLastCommentDatePresetDates } from "components/DateFilterSelect/constants";
import "./styles.css";

const DATE_FIELD_NAMES = ["noticeDate", "eventDate", "leaseEndDate", "lastCommentDate"];
const FilterModal = ({
    open,
    onClose,
    filters = [],
    selectedFilters = {},
    onApplyFilter = () => {},
    onClearFilter = () => {},
}) => {
    const [localFilters, setLocalFilters] = useState({});

    useEffect(() => {
        if (open) {
            if (selectedFilters && typeof selectedFilters === "object" && !Array.isArray(selectedFilters)) {
                setLocalFilters({ ...selectedFilters });
            } else {
                setLocalFilters({});
            }
        }
    }, [open, selectedFilters]);

    const handleFilterChange = (filterName, selectedValues) => {
        setLocalFilters((prev) => ({
            ...prev,
            [filterName]: selectedValues,
        }));
    };

    const handleDateChange = (dateField, value) => {
        setLocalFilters((prev) => {
            if (!value || (value.dateType === null || value.dateType === undefined)) {
                const next = { ...prev };
                delete next[dateField];
                return next;
            }
            return { ...prev, [dateField]: value };
        });
    };

    const handleApply = () => {
        const finalFilters = { ...localFilters };
        Object.keys(finalFilters).forEach((key) => {
            const value = finalFilters[key];
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
                delete finalFilters[key];
            }
        });
        onApplyFilter(finalFilters);
        onClose();
    };

    const handleClear = () => {
        // Notice date always carries a default (All / within-N-months); "Clear" resets
        // every other filter but leaves it as-is, matching the previous header-row behavior.
        setLocalFilters((prev) => (prev.noticeDate ? { noticeDate: prev.noticeDate } : {}));
    };

    const getFilterByName = (name) => {
        return filters.find((filter) => filter.name === name);
    };

    const getFilterOptions = (filterName) => {
        const filter = getFilterByName(filterName);
        if (!filter || !filter.options) return [];

        if (filter.idMap && filter.idMap.length > 0) {
            return filter.idMap
                .map((item) => ({
                    label: filter.mapName ? item[filter.mapName] : item.name,
                    value: filter.mapId ? item[filter.mapId] : item.id,
                    ...item,
                }))
                .filter((option) => {
                    const label = option.label;
                    const value = option.value;
                    return (
                        label !== null &&
                        label !== undefined &&
                        label !== "" &&
                        label.trim() !== "" &&
                        value !== null &&
                        value !== undefined &&
                        value !== ""
                    );
                });
        }

        return filter.options
            .filter(
                (option) =>
                    option !== null &&
                    option !== undefined &&
                    option !== "" &&
                    option.trim() !== ""
            )
            .map((option) => ({
                label: option,
                value: option,
            }));
    };

    const getSelectedValues = (filterName) => {
        const selectedValues = localFilters[filterName] || [];
        const options = getFilterOptions(filterName);
        return selectedValues.map((value) => {
            const option = options.find((opt) => opt.value === value);
            return option || { label: value, value };
        });
    };

    const getFilterCount = () => {
        let count = 0;
        filters.forEach((filter) => {
            if (DATE_FIELD_NAMES.includes(filter.name)) return;
            const values = localFilters[filter.name];
            if (values && values.length > 0) {
                count++;
            }
        });

        // Notice date always has a default value (All / within-N-months) so it's excluded
        // from the count, same as when it lived in the header row instead of this panel.
        ["eventDate", "leaseEndDate", "lastCommentDate"].forEach((dateField) => {
            const dr = localFilters[dateField];
            if (dr && dr.dateType !== null && dr.dateType !== undefined) count++;
        });

        return count;
    };

    const hasChanges = useMemo(() => {
        const currentState = { ...localFilters };
        const existingState = { ...selectedFilters };
        Object.keys(currentState).forEach((key) => currentState[key] === undefined && delete currentState[key]);
        Object.keys(existingState).forEach((key) => existingState[key] === undefined && delete existingState[key]);
        const allKeys = [...new Set([...Object.keys(currentState), ...Object.keys(existingState)])];
        if (allKeys.length === 0) return false;
        for (let key of allKeys) {
            const currentValue = currentState[key];
            const existingValue = existingState[key];
            const normalizeValue = (value) => (Array.isArray(value) && value.length === 0 ? undefined : value);
            const normalizedCurrent = normalizeValue(currentValue);
            const normalizedExisting = normalizeValue(existingValue);
            if (Array.isArray(normalizedCurrent) && Array.isArray(normalizedExisting)) {
                if (normalizedCurrent.length !== normalizedExisting.length) return true;
                const sortedCurrent = [...normalizedCurrent].sort();
                const sortedExisting = [...normalizedExisting].sort();
                if (JSON.stringify(sortedCurrent) !== JSON.stringify(sortedExisting)) return true;
            } else if (
                normalizedCurrent !== null &&
                typeof normalizedCurrent === "object" &&
                !Array.isArray(normalizedCurrent)
            ) {
                if (JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedExisting)) return true;
            } else if (normalizedCurrent !== normalizedExisting) {
                return true;
            }
        }
        return false;
    }, [localFilters, selectedFilters]);

    const handleModalClose = () => {
        setLocalFilters({});
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => {}}
            className="lease-events-filter-modal"
            sx={{
                "& .MuiDrawer-paper": {
                    width: "360px",
                    maxWidth: "360px",
                    height: "100vh",
                    position: "fixed",
                    top: 0,
                    right: 0,
                    zIndex: 1300,
                },
                "& .MuiBackdrop-root": {
                    backgroundColor: "rgba(51, 68, 74, 0.30)",
                    pointerEvents: "none",
                },
            }}
        >
            <Box className="lease-events-filter-modal-header-title" sx={{ p: 2, borderBottom: "1px solid #E5E7EB" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" sx={{ fontFamily: "'Source Sans Pro'" }}>
                            Filters
                        </Typography>
                        {getFilterCount() > 0 && (
                            <Box className="lease-events-filter-modal-count-badge">{getFilterCount()}</Box>
                        )}
                    </Box>
                    <IconButton onClick={handleModalClose} size="small">
                        <CloseIcon sx={{ color: "#01151D", fontSize: "20px", borderRadius: "6px" }} />
                    </IconButton>
                </Box>
                <Box className="lease-events-filter-modal-header-buttons" alignItems="center">
                    <Button
                        variant="text"
                        onClick={handleClear}
                        className="lease-events-filter-modal-clear-button"
                        size="small"
                        disabled={getFilterCount() === 0}
                        sx={{ fontFamily: "'Source Sans Pro'" }}
                    >
                        Clear
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        className="lease-events-filter-modal-apply-button"
                        size="small"
                        disabled={!hasChanges}
                        sx={{ fontFamily: "'Source Sans Pro'" }}
                    >
                        Apply
                    </Button>
                </Box>
            </Box>

            <Box className="lease-events-filter-modal-content" sx={{ overflow: 'auto', height: 'calc(100vh - 140px)' }}>
                {/* LEASE EVENT DETAILS Section */}
                <Box className="lease-events-filter-section">
                    <Typography className="lease-events-filter-section-title">
                        LEASE EVENT DETAILS
                    </Typography>
                    {filters
                        .filter((filter) =>
                            [
                                "names",
                                "leaseTypes",
                                "leaseStatuses",
                                "eventTypes",
                                "eventStatuses",
                                "tenureTypes",
                                "propertyTypes",
                                "businessUnits",
                            ].includes(filter.name)
                        )
                        .map((filter) => (
                            <MultiSelect
                                key={filter.name}
                                id={`lease-event-filter-${filter.name}`}
                                name={filter.name}
                                label={filter.label}
                                options={getFilterOptions(filter.name)}
                                selected={getSelectedValues(filter.name)}
                                onSelect={(values) => handleFilterChange(filter.name, values.map((v) => v.value))}
                                placeholder="Start typing or select"
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                maxDisplayChips={2}
                            />
                        ))}
                </Box>

                {/* PROPERTY ADDRESS Section */}
                <Box className="lease-events-filter-section">
                    <Typography className="lease-events-filter-section-title">
                        PROPERTY ADDRESS
                    </Typography>
                    {filters
                        .filter((filter) =>
                            ["clientRegions", "countries", "stateProvinces", "cities"].includes(filter.name)
                        )
                        .map((filter) => (
                            <MultiSelect
                                key={filter.name}
                                id={`lease-event-filter-${filter.name}`}
                                name={filter.name}
                                label={filter.label}
                                options={getFilterOptions(filter.name)}
                                selected={getSelectedValues(filter.name)}
                                onSelect={(values) => handleFilterChange(filter.name, values.map((v) => v.value))}
                                placeholder="Start typing or select"
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.value}
                                maxDisplayChips={2}
                            />
                        ))}
                </Box>

                {/* LEASE EVENTS DATES Section */}
                <Box className="lease-events-filter-section">
                    <Typography className="lease-events-filter-section-title">
                        LEASE EVENTS DATES
                    </Typography>

                    <DateFilterSelect
                        label="Notice date"
                        value={localFilters.noticeDate || null}
                        onChange={(val) => handleDateChange("noticeDate", val)}
                        options={NOTICE_DATE_FILTER_OPTIONS}
                        resolveDates={resolveNoticeDatePresetDates}
                    />

                    <DateFilterSelect
                        label="Event date"
                        value={localFilters.eventDate || null}
                        onChange={(val) => handleDateChange("eventDate", val)}
                    />

                    <DateFilterSelect
                        label="Lease end date"
                        value={localFilters.leaseEndDate || null}
                        onChange={(val) => handleDateChange("leaseEndDate", val)}
                    />

                    <DateFilterSelect
                        label="Last comment date"
                        value={localFilters.lastCommentDate || null}
                        onChange={(val) => handleDateChange("lastCommentDate", val)}
                        options={LAST_COMMENT_DATE_FILTER_OPTIONS}
                        resolveDates={resolveLastCommentDatePresetDates}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};

export default React.memo(FilterModal); 